<template>
  <VaSidebar v-model="writableVisible" :width="sidebarWidth" :color="color" minimized-width="0">
    <VaAccordion v-model="value" multiple>
      <VaCollapse v-for="(navigationRoute, routeIndex) in navigationRoutes.routes" :key="routeIndex">
        <template #header="{ value: isCollapsed }">
          <VaSidebarItem
            :to="navigationRoute.children ? undefined : { name: navigationRoute.name }"
            :active="routeHasActiveChild(navigationRoute)"
            :active-color="activeColor"
            :text-color="textColor(navigationRoute)"
            :aria-label="`${navigationRoute.children ? 'Open category ' : 'Visit'} ${navigationRoute.displayName}`"
            role="button"
            hover-opacity="0.10"
          >
            <VaSidebarItemContent class="py-3 pl-4 pr-2">
              <VaIcon
                v-if="navigationRoute.meta.icon"
                aria-hidden="true"
                :name="navigationRoute.meta.icon"
                size="20px"
                :color="iconColor(navigationRoute)"
              />
              <VaSidebarItemTitle class="flex items-center justify-between font-semibold leading-5">
                {{ navigationRoute.displayName }}
                <VaIcon v-if="navigationRoute.children" :name="arrowDirection(isCollapsed)" size="20px" />
              </VaSidebarItemTitle>
            </VaSidebarItemContent>
          </VaSidebarItem>
        </template>
        <template #body>
          <div v-for="(childRoute, childIndex) in navigationRoute.children" :key="childIndex">
            <VaSidebarItem
              :to="{ name: childRoute.name }"
              :active="isActiveChildRoute(childRoute)"
              :active-color="activeColor"
              :text-color="textColor(childRoute)"
              :aria-label="`Visit ${navigationRoute.displayName}`"
              hover-opacity="0.10"
            >
              <VaSidebarItemContent class="py-3 pl-11 pr-2">
                <VaSidebarItemTitle class="font-semibold leading-5">
                  {{ childRoute.displayName }}
                </VaSidebarItemTitle>
              </VaSidebarItemContent>
            </VaSidebarItem>
          </div>
        </template>
      </VaCollapse>
    </VaAccordion>
  </VaSidebar>
</template>
<script lang="ts" setup>
import { ref, computed, watch } from "vue";
import { useRoute } from "vue-router";
import { useColors } from "vuestic-ui";

import navigationRoutes, { type INavigationRoute } from "./NavigationRoutes";

const props = defineProps({
  visible: { type: Boolean, default: true },
  mobile: { type: Boolean, default: false },
});

const emit = defineEmits(["update:visible"]);

const { getColor, colorToRgba } = useColors();
const route = useRoute();

const value = ref<boolean[]>([]);

const writableVisible = computed({
  get: () => props.visible,
  set: (v: boolean) => emit("update:visible", v),
});

const isActiveChildRoute = (child: INavigationRoute) => route.path.startsWith(`/${child.name}`);

const routeHasActiveChild = (section: INavigationRoute) => {
  if (!section.children) {
    return route.path.startsWith(`/${section.name}`);
  }

  return section.children.some((child) => route.path.startsWith(`/${child.name}`));
};

const setActiveExpand = () =>
  (value.value = navigationRoutes.routes.map((route: INavigationRoute) => routeHasActiveChild(route)));

const sidebarWidth = computed(() => (props.mobile ? "100vw" : "280px"));
const color = computed(() => getColor("background-secondary"));
const activeColor = computed(() => colorToRgba(getColor("focus"), 0.1));

const iconColor = (route: INavigationRoute) => (routeHasActiveChild(route) ? "primary" : "secondary");
const textColor = (route: INavigationRoute) => (routeHasActiveChild(route) ? "primary" : "textPrimary");
const arrowDirection = (state: boolean) => (state ? "va-arrow-up" : "va-arrow-down");

watch(() => route.fullPath, setActiveExpand, { immediate: true });
</script>
